import { useContext, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material'
import TableComponent from '../../../shared/Table'
import { ExercisesConfigs } from '../../../../utils/constants'
import {
  createExercise,
  deleteExercise,
} from '../../../../services/spectrum-manager.service'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { useToast } from '../../../../contexts/toast.context'
import { IExercise } from '../../../../types/exercise.type'
import { AppConfigContext } from '../../../../contexts/app-config.context'
import { RequestContext } from '../../../../contexts/request.context'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'

import styles from './styles.module.scss'

export const Exercises = () => {
  const [isAdding, setIsAdding] = useState(false)
  const [exercise, setExercise] = useState<IExercise | null>(null)
  const [dateError, setDateError] = useState<string | null>(null)

  const { showToast } = useToast()
  const { isLightMode } = useContext(AppConfigContext)
  const { exercises, setExercises } = useContext(RequestContext)

  const handleExerciseChange = (key: string, value: string | Dayjs | null) => {
    const unit = exercise ? exercise : ({} as IExercise)
    setExercise({ ...unit, [key]: value })
  }

  const handleDateChange = (
    key: 'start_date' | 'end_date',
    value: Dayjs | null,
  ) => {
    if (key === 'start_date' && exercise?.end_date) {
      if (value && exercise.end_date.isBefore(value)) {
        setDateError('Start date cannot be after end date')
      } else {
        setDateError(null)
      }
    }

    if (key === 'end_date' && exercise?.start_date) {
      if (value && exercise.start_date.isAfter(value)) {
        setDateError('End date cannot be before start date')
      } else {
        setDateError(null)
      }
    }

    handleExerciseChange(key, value)
  }

  const onClose = () => {
    setIsAdding(false)
  }

  const handleExerciseCreate = async () => {
    if (exercise && !dateError) {
      const data = await createExercise(exercise)

      if (data.data) {
        setExercises([
          ...exercises,
          {
            ...data.data,
            start_date: dayjs(data.data.start_date).utc(true),
            end_date: dayjs(data.data.end_date).utc(true),
          },
        ])
        showToast('success', 'Exercise created successfully')
        onClose()
      }
    }
  }

  const handleRemoveExercise = async (id: number) => {
    const data = await deleteExercise(id)

    if (data.success) {
      setExercises(exercises.filter((exercise) => exercise.id !== id))
      showToast('success', 'Exercise removed successfully')
    }
  }

  return (
    <Box className={styles.exercises}>
      <TableComponent
        isLightMode={isLightMode}
        data={exercises}
        handleCreate={() => setIsAdding(true)}
        configs={ExercisesConfigs}
        handleRefetch={() => {}}
        handleDelete={handleRemoveExercise}
      />
      <Modal className={styles.modal} open={isAdding} onClose={onClose}>
        <Box className={styles.modalContent}>
          <Box className={styles.header}>
            <Typography data-testid="title" className={styles.title}>
              Add Exercise
            </Typography>
            <IconButton data-testid="closeButton" onClick={onClose}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
          <Box className={styles.content}>
            <Box className={styles.addExercise}>
              <Box className={styles.fieldContainer}>
                <Typography className={styles.label}>
                  Name:
                  <Typography className={styles.asterisk}>*</Typography>
                </Typography>
                <TextField
                  label="Name"
                  className={styles.field}
                  value={exercise?.name || ''}
                  onChange={(evt) =>
                    handleExerciseChange('name', evt.target.value)
                  }
                />
              </Box>
              <Box className={styles.fieldContainer}>
                <Typography className={styles.label}>
                  Start Date:
                  <Typography className={styles.asterisk}>*</Typography>
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    className={styles.field}
                    value={exercise?.start_date ? exercise.start_date : null}
                    sx={{ color: isLightMode ? 'black' : 'white' }}
                    onChange={(newValue) =>
                      handleDateChange('start_date', dayjs(newValue).utc(true))
                    }
                  />
                </LocalizationProvider>
              </Box>
              <Box className={styles.fieldContainer}>
                <Typography className={styles.label}>
                  End Date:
                  <Typography className={styles.asterisk}>*</Typography>
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="End Date"
                    className={styles.field}
                    value={exercise?.end_date ? exercise.end_date : null}
                    sx={{ color: isLightMode ? 'black' : 'white' }}
                    onChange={(newValue) =>
                      handleDateChange('end_date', dayjs(newValue).utc(true))
                    }
                  />
                </LocalizationProvider>
                {dateError && (
                  <Typography color="error" className={styles.errorText}>
                    {dateError}
                  </Typography>
                )}
              </Box>
              <Box className={styles.fieldContainer}>
                <Typography className={styles.label}>F 702</Typography>
                <TextField
                  placeholder="Enter Value"
                  className={styles.field}
                  value={exercise?.f_702}
                  onChange={(evt) =>
                    handleExerciseChange('f_702', evt.target.value)
                  }
                />
              </Box>
              <Box className={styles.fieldContainer}>
                <Typography className={styles.label}>
                  F 910
                  <Typography className={styles.asterisk}>*</Typography>:
                </Typography>
                <TextField
                  placeholder="Enter Value"
                  className={styles.field}
                  value={exercise?.f_910}
                  onChange={(evt) =>
                    handleExerciseChange('f_910', evt.target.value)
                  }
                />
              </Box>
            </Box>
          </Box>
          <Box className={styles.actions}>
            <Button
              id="confirmButton"
              data-testid="confirmButton"
              className={styles.removeBtn}
              onClick={handleExerciseCreate}
            >
              Create
            </Button>
            <Button
              className={styles.cancelBtn}
              color="inherit"
              onClick={onClose}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}
